// Append-only event log for the scaffold — decisions, risks, todos, notes.
//
// Adapted from mex (MIT, Copyright (c) 2026 Daksh Jaitly) — re-implemented on
// CAI's own config/layout primitives rather than mex's memory layer. The log is
// a single JSONL file at the scaffold root; one event per line, never rewritten.

import { appendFileSync, existsSync, readFileSync } from "node:fs";
import { resolve } from "node:path";
import type { CaiConfig } from "./types.js";

export type EventKind = "decision" | "risk" | "todo" | "note";

export const EVENT_KINDS: readonly EventKind[] = ["decision", "risk", "todo", "note"];

export interface ScaffoldEvent {
  /** ISO timestamp of when the event was logged. */
  at: string;
  kind: EventKind;
  message: string;
}

/** Absolute path to the event log file. */
export function eventLogPath(config: CaiConfig): string {
  return resolve(config.scaffoldRoot, "events.jsonl");
}

/** Append one event to the log. Returns the written event. */
export function logEvent(
  config: CaiConfig,
  kind: EventKind,
  message: string,
  now = new Date()
): ScaffoldEvent {
  const event: ScaffoldEvent = { at: now.toISOString(), kind, message };
  // Defensive: the scaffold directory may have been removed concurrently
  // (e.g. a live-watch teardown racing with a final async tick). Failing
  // open here keeps the watcher's read-only loop from throwing unhandled
  // rejections during shutdown.
  try {
    appendFileSync(eventLogPath(config), JSON.stringify(event) + "\n");
  } catch {
    // best-effort — caller already has the event object
  }
  return event;
}

/** Read all logged events, oldest first. Malformed lines are skipped. */
export function readEvents(config: CaiConfig): ScaffoldEvent[] {
  const path = eventLogPath(config);
  if (!existsSync(path)) return [];
  const events: ScaffoldEvent[] = [];
  for (const line of readFileSync(path, "utf-8").split("\n")) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    try {
      const parsed = JSON.parse(trimmed);
      if (isValidEvent(parsed)) events.push(parsed);
    } catch {
      // skip malformed line
    }
  }
  return events;
}

function isValidEvent(value: unknown): value is ScaffoldEvent {
  if (typeof value !== "object" || value === null) return false;
  const e = value as Record<string, unknown>;
  return (
    typeof e.at === "string" &&
    typeof e.message === "string" &&
    typeof e.kind === "string" &&
    (EVENT_KINDS as readonly string[]).includes(e.kind)
  );
}
